// ─── ReviewPipelineTable ─────────────────────────────────────────────────
// The deals grid under the funnel on the Review Pipeline page.
//
// Each row is clickable — click to expand a detail panel with:
//   • kill_reason / kill_narrative
//   • deal_competition detail (our bid $/SF + total, winning bid $/SF +
//     total, delta, winner, num bidders, made B&F, broker debrief)
//   • market clearing (from deals.market_cleared_* if the passed deal
//     has already traded — surfaced from the Comps > Passed Deals
//     Awaiting Sale flow)
//   • Edit button (kill reason / narrative / B&F flag) writing straight
//     to the `deals` table so edits made here propagate everywhere
//
// Data shape: the `rows` prop is the array from computeDealsAndListings;
// `deals` is the full loaded deals list; `competition` is the array of
// deal_competition rows keyed by deal_id. `onDealEdited(updatedDeal)`
// bubbles the saved patch back so the caller can update local state.

import { useMemo, useState } from "react";
import BuyBoxChip from "../common/BuyBoxChip";
import { supabase, dbToDeal } from "../../supabase";
import { KILL_REASONS, KILLED_STAGES } from "../../constants/dealStages";
import { inputCls } from "../common/FormHelpers";

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtSf(n)  { return n ? `${Number(n).toLocaleString()} SF` : "—"; }
function fmtPct(n) { return n && Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "—"; }
function fmtPsf(n) { return n && Number.isFinite(Number(n)) ? `$${Number(n).toFixed(2)}/SF` : "—"; }
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

function KV({ label, value, accent = false }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-[rgba(58,58,58,0.4)] last:border-0">
      <span className="text-[11px] uppercase tracking-wider text-[#8a8a8a] font-semibold">{label}</span>
      <span className={`text-xs text-right tabular-nums ${accent ? "text-pp-red font-semibold" : "text-[#f4f4f4]"}`}>{value}</span>
    </div>
  );
}

function InlineDealEditor({ deal, onClose, onSaved }) {
  const [madeBestAndFinal, setMadeBestAndFinal] = useState(!!deal.madeBestAndFinal);
  const [killedReason, setKilledReason] = useState(deal.killedReason || "");
  const [killedNarrative, setKilledNarrative] = useState(deal.killedNarrative || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isKilled = KILLED_STAGES.includes(deal.stage);

  async function save() {
    setSaving(true); setError(null);
    const patch = { made_best_and_final: !!madeBestAndFinal };
    if (isKilled) {
      patch.killed_reason    = killedReason?.trim()    || null;
      patch.killed_narrative = killedNarrative?.trim() || null;
    }
    const { data, error: e } = await supabase
      .from("deals").update(patch).eq("id", deal.id).select().single();
    setSaving(false);
    if (e) { setError(e.message); return; }
    onSaved(dbToDeal(data));
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-[#242424] border border-[#3a3a3a] rounded-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-bold text-white font-montserrat">Edit deal notes</h3>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-[#f4f4f4]">✕</button>
        </div>
        <div className="text-xs text-[#8a8a8a] mb-4">{deal.addresses?.[0] || "(unnamed)"}</div>

        <label className="inline-flex items-center gap-2 text-sm text-[#f4f4f4] cursor-pointer select-none mb-4">
          <input type="checkbox" checked={madeBestAndFinal}
            onChange={(e) => setMadeBestAndFinal(e.target.checked)}
            className="w-4 h-4 accent-pp-red" />
          <span className="font-semibold">Made it to Best &amp; Final</span>
          <span className="text-xs text-[#8a8a8a] font-normal">(implies IC met)</span>
        </label>

        {isKilled && (
          <>
            <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Kill Reason</label>
            <select value={killedReason} onChange={(e) => setKilledReason(e.target.value)} className={inputCls() + " mb-3"}>
              <option value="">— none —</option>
              {(KILL_REASONS[deal.stage] || []).map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Narrative</label>
            <textarea value={killedNarrative} onChange={(e) => setKilledNarrative(e.target.value)}
              rows={5} className={inputCls() + " mb-3"}
              placeholder="What happened, who won, what did we learn…" />
          </>
        )}

        {error && <div className="text-xs text-[#f04f44] mb-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#b4b4b4] hover:text-[#f4f4f4]">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg bg-pp-red hover:bg-pp-red-light text-white font-semibold disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Row detail panel — rendered inline when a row is expanded ─────
function RowDetail({ deal, comp, onEdit, onNavigate }) {
  const psfDelta = comp?.delta_to_winner_psf;
  const pctDelta = comp?.delta_pct;
  return (
    <div className="col-span-full px-6 py-4 bg-[rgba(36,36,36,0.7)] border-t border-[rgba(58,58,58,0.4)]">
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#8a8a8a] mb-2">Deal Detail</div>
          <KV label="Address"       value={deal.addresses?.[0] || "—"} />
          <KV label="MSA"           value={(deal.msas || []).join(", ") || "—"} />
          <KV label="SF"            value={fmtSf(deal.squareFeet)} />
          <KV label="Ask"           value={fmtMoney(deal.askingPrice)} />
          <KV label="Broker"        value={[deal.brokerName, deal.brokerFirm].filter(Boolean).join(" · ") || "—"} />
          <KV label="Made B&F"      value={deal.madeBestAndFinal ? "Yes" : "No"} accent={deal.madeBestAndFinal} />
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#8a8a8a] mb-2">Kill Notes</div>
          <KV label="Stage"     value={deal.stage} />
          <KV label="Killed"    value={fmtDate(deal.killedAt)} />
          <KV label="Reason"    value={deal.killedReason || "—"} />
          <div className="mt-2 text-xs text-[#b4b4b4] whitespace-pre-wrap min-h-[36px] p-2 rounded bg-[rgba(28,28,28,0.4)] border border-[rgba(58,58,58,0.4)]">
            {deal.killedNarrative || <span className="text-[#8a8a8a] italic">(no narrative)</span>}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#8a8a8a] mb-2">Competition + Market</div>
          {comp ? (
            <>
              <KV label="Bidders"       value={comp.num_bidders_estimated ?? "—"} />
              <KV label="Our $/SF"      value={fmtPsf(comp.our_final_bid_psf)} />
              <KV label="Our Total"     value={fmtMoney(comp.our_final_bid_total)} />
              <KV label="Winner $/SF"   value={fmtPsf(comp.winning_bid_psf)} />
              <KV label="Winner Total"  value={fmtMoney(comp.winning_bid_total)} />
              <KV label="Δ $/SF"        value={fmtPsf(psfDelta)}
                accent={psfDelta != null && Math.abs(psfDelta) > 0} />
              <KV label="Δ %"           value={pctDelta == null ? "—" : `${Number(pctDelta).toFixed(1)}%`}
                accent={pctDelta != null} />
              <KV label="Winner"        value={comp.winner_name || "—"} />
              <KV label="Lost reason"   value={comp.lost_reason || "—"} />
            </>
          ) : (
            <div className="text-xs text-[#8a8a8a] italic">No competition record.</div>
          )}
          {(deal.marketClearedAt || deal.marketClearedPrice) && (
            <div className="mt-3 pt-3 border-t border-[rgba(58,58,58,0.6)]">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Market Clearing</div>
              <KV label="Actual sale"   value={fmtMoney(deal.marketClearedPrice)} />
              <KV label="Actual $/SF"   value={fmtPsf(deal.marketClearedPricePsf)} />
              <KV label="Buyer"         value={deal.marketClearedBuyer || "—"} />
              <KV label="Closed"        value={fmtDate(deal.marketClearedAt)} />
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 justify-end">
        <button onClick={() => onEdit(deal)}
          className="text-[11px] px-2 py-1 rounded border border-[#4a4a4a] text-[#b4b4b4] hover:border-pp-red hover:text-white">
          Edit notes / B&F
        </button>
        {onNavigate && (
          <button onClick={() => onNavigate(deal.id)}
            className="text-[11px] px-2 py-1 rounded border border-pp-red/60 text-pp-red hover:bg-pp-red/20">
            Open deal →
          </button>
        )}
      </div>
    </div>
  );
}

export default function ReviewPipelineTable({ rows, deals, competition = [], onNavigateToDeal, onDealEdited }) {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const dealsById = useMemo(() => new Map(deals.map(d => [d.id, d])), [deals]);
  const compsById = useMemo(() => new Map((competition || []).map(c => [c.deal_id, c])), [competition]);

  const visible = useMemo(() => {
    const search = q.trim().toLowerCase();
    return rows.filter(r => {
      if (search) {
        const hay = `${r.address || ""} ${(r.msas || []).join(" ")} ${r.broker || ""} ${r.stage || ""} ${r.killedReason || ""}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });
  }, [rows, q]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search address, broker, MSA, kill reason…"
          className="flex-1 min-w-[220px] bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-[#e93529]" />
        <span className="text-xs text-[#8a8a8a] ml-2">{visible.length} of {rows.length}</span>
      </div>

      <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.5)] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[24px_minmax(220px,1fr)_140px_130px_90px_100px_80px_110px_100px] gap-3 px-4 py-3 border-b border-[rgba(58,58,58,0.5)] text-[10px] font-bold text-[#8a8a8a] uppercase tracking-wider">
          <div />
          <div>Address</div>
          <div>MSA</div>
          <div>Stage</div>
          <div>Buy-Box</div>
          <div className="text-right">SF</div>
          <div className="text-right">Cap</div>
          <div className="text-right">Ask</div>
          <div>Date</div>
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-[#8a8a8a] p-6 text-center">No deals match.</p>
        ) : visible.map(r => {
          const deal = dealsById.get(r.id);
          const comp = compsById.get(r.id);
          const isOpen = expandedId === r.id;
          return (
            <div key={r.id} className="border-b border-[rgba(58,58,58,0.3)]">
              <button
                onClick={() => setExpandedId(isOpen ? null : r.id)}
                className="w-full text-left grid grid-cols-[24px_minmax(220px,1fr)_140px_130px_90px_100px_80px_110px_100px] gap-3 px-4 py-2 items-center text-sm hover:bg-[rgba(44,44,44,0.4)] transition-colors">
                <div className="text-[#8a8a8a] text-xs">{isOpen ? "▾" : "▸"}</div>
                <div className="min-w-0">
                  <div className="text-[#f4f4f4] truncate">{r.address || "—"}</div>
                  <div className="text-[10px] text-[#8a8a8a] truncate">
                    {r.broker || ""}
                    {r.killedReason && <span className="ml-2 text-[#8a8a8a] italic">{r.killedReason}</span>}
                    {deal?.madeBestAndFinal && <span className="ml-2 text-pp-red font-semibold">· B&amp;F</span>}
                  </div>
                </div>
                <div className="text-[#b4b4b4] text-xs truncate">{(r.msas || []).join(", ") || "—"}</div>
                <div className="text-[#b4b4b4] text-xs truncate">{r.stage || <span className="text-[#8a8a8a]">—</span>}</div>
                <div>
                  <BuyBoxChip verdict={r.buyBoxVerdict} source={r.buyBoxSource} reason={r.buyBoxReason} size="sm" />
                </div>
                <div className="text-[#b4b4b4] text-xs text-right">{fmtSf(r.sf)}</div>
                <div className="text-[#b4b4b4] text-xs text-right">{fmtPct(r.capRate)}</div>
                <div className="text-[#b4b4b4] text-xs text-right">{fmtMoney(r.askingPrice)}</div>
                <div className="text-[#8a8a8a] text-xs">{fmtDate(r.created_at)}</div>
              </button>
              {isOpen && deal && (
                <RowDetail deal={deal} comp={comp} onEdit={setEditing} onNavigate={onNavigateToDeal} />
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <InlineDealEditor
          deal={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => { onDealEdited?.(updated); }}
        />
      )}
    </div>
  );
}
